import { loadStripe } from '@stripe/stripe-js';
import { collection, addDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { firestore as db } from './firebase';

// 从环境变量加载Stripe公钥
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: string;
  interval: 'month' | 'year';
  currency: string;
  stripePriceId: string;
  features: string[];
}

// 定义订阅计划
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: '免费版',
    price: '0',
    interval: 'month',
    currency: 'usd',
    stripePriceId: '', // 免费版不需要Stripe价格ID
    features: [
      '基本功能',
      '每日使用限制',
      '社区支持',
      '两周试用期'
    ]
  },
  {
    id: 'monthly',
    name: '月度订阅',
    price: '0.99',
    interval: 'month',
    currency: 'usd',
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY as string, // 从环境变量获取
    features: [
      '全部高级功能',
      '无使用限制',
      '优先客户支持',
      '提前使用新功能'
    ]
  },
  {
    id: 'yearly',
    name: '年度订阅',
    price: '9.9',
    interval: 'year',
    currency: 'usd',
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_YEARLY as string, // 从环境变量获取
    features: [
      '全部高级功能',
      '无使用限制',
      '比月付节省近17%',
      '优先客户支持',
      '提前使用新功能'
    ]
  }
];

// 创建Stripe结账会话
export const createCheckoutSession = async (
  userId: string, 
  priceId: string,
  successUrl = window.location.origin + '/subscription?success=true',
  cancelUrl = window.location.origin + '/pricing'
) => {
  try {
    // 创建结账会话文档
    const docRef = await addDoc(collection(db, 'users', userId, 'checkout_sessions'), {
      price: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
    });

    // 等待Cloud Functions处理并更新文档
    let checkoutSessionRef;
    let maxTries = 10;
    let tries = 0;
    
    while (tries < maxTries) {
      checkoutSessionRef = await getDoc(doc(db, 'users', userId, 'checkout_sessions', docRef.id));
      const checkoutSession = checkoutSessionRef.data();
      
      if (checkoutSession && checkoutSession.url) {
        // 重定向到Stripe托管的结账页面
        window.location.assign(checkoutSession.url);
        return;
      } else if (checkoutSession && checkoutSession.error) {
        throw new Error(`创建结账会话失败: ${checkoutSession.error.message}`);
      }
      
      // 等待一段时间再重试
      await new Promise(resolve => setTimeout(resolve, 300));
      tries++;
    }
    
    throw new Error('结账会话创建超时');
  } catch (error) {
    console.error('创建结账会话时出错:', error);
    throw error;
  }
};

// 获取用户的订阅信息
export const getUserSubscription = async (userId: string) => {
  try {
    const subscriptionRef = doc(db, 'users', userId, 'subscriptions', 'active');
    const subscriptionDoc = await getDoc(subscriptionRef);
    
    if (subscriptionDoc.exists()) {
      return subscriptionDoc.data();
    }
    
    return null;
  } catch (error) {
    console.error('获取用户订阅时出错:', error);
    return null;
  }
};

// 检查用户是否有订阅或处于试用期
export const checkUserSubscription = async (userId: string) => {
  try {
    const subscription = await getUserSubscription(userId);
    
    if (!subscription) {
      return {
        isSubscribed: false,
        isTrialing: false,
        status: 'inactive',
      };
    }
    
    const isActive = ['active', 'trialing'].includes(subscription.status);
    const isTrialing = subscription.status === 'trialing';
    
    return {
      isSubscribed: isActive,
      isTrialing,
      status: subscription.status,
      plan: subscription.planId,
      trialEnd: subscription.trial_end?.toDate(),
      currentPeriodEnd: subscription.current_period_end?.toDate(),
    };
  } catch (error) {
    console.error('检查用户订阅时出错:', error);
    return {
      isSubscribed: false,
      isTrialing: false,
      status: 'error',
    };
  }
}; 